import { unaryRpc } from "@redotech/rpc/definition";
import * as z from "zod";
import { BackfillSyncSeveritySchema } from "./get-support-backfill-messages";

/** Scheduled runs are left out: they fan out to one run per integration several times a day. */
export const BackfillRunSyncTypeSchema = z.enum(["initial_backfill", "manual"]);
export type BackfillRunSyncType = z.infer<typeof BackfillRunSyncTypeSchema>;

/** `shopper_ai_integration.integration_type` values that run backfills. */
export const BackfillRunIntegrationTypeSchema = z.enum([
  "gmail",
  "outlook",
  "custom_email",
  "facebook",
  "instagram",
]);
export type BackfillRunIntegrationType = z.infer<
  typeof BackfillRunIntegrationTypeSchema
>;

const backfillRunSchema = z.object({
  id: z.string(),
  syncType: BackfillRunSyncTypeSchema,
  severity: BackfillSyncSeveritySchema.nullable(),
  integrationId: z.string(),
  integrationType: z.string(),
  /** Email address or page name of the connected integration, when its channel row exists. */
  integrationName: z.string().nullable(),
  teamId: z.string(),
  teamName: z.string().nullable(),
  syncEarlyDate: z.date(),
  syncLateDate: z.date(),
  syncCursor: z.date().nullable(),
  finished: z.boolean(),
  /** Share of the backfill window walked so far, from 0 to 1. */
  progress: z.number().min(0).max(1),
  /** Projected finish if the run keeps its pace so far; null before any progress and once finished. */
  estimatedFinishAt: z.date().nullable(),
  createdAt: z.date(),
});

export const getSupportBackfillRuns = unaryRpc({
  name: "getSupportBackfillRuns" as const,
  input: z
    .object({
      startDate: z.date(),
      endDate: z.date(),
      syncTypes: z.array(BackfillRunSyncTypeSchema).min(1),
      integrationTypes: z.array(BackfillRunIntegrationTypeSchema).optional(),
      limit: z.number().int().min(1).max(200).optional(),
    })
    .refine(({ startDate, endDate }) => endDate > startDate, {
      message: "End date must be after start date.",
    }),
  output: z.object({
    totalCount: z.number().int().min(0),
    daily: z.array(
      z.object({
        date: z.date(),
        count: z.number().int().min(0),
        bySyncType: z.record(z.string(), z.number().int().min(0)),
        byIntegrationType: z.record(z.string(), z.number().int().min(0)),
      }),
    ),
    recentRuns: z.array(backfillRunSchema),
  }),
});

export type GetSupportBackfillRunsInput = z.infer<
  typeof getSupportBackfillRuns.input
>;

export type GetSupportBackfillRunsOutput = z.infer<
  typeof getSupportBackfillRuns.output
>;
